import { ChangeDetectorRef, Component, ViewEncapsulation } from '@angular/core';
import { mdTransitionAnimation, MenuController, NavController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '@core/services/system/modal.service';
import { DbDaoService } from '@core/services/db/db-dao.service';
import { UpdatesService } from '@core/services/system/updates.service';
import { SyncService } from '@core/services/system/sync.service';
import { SecurityService } from '@core/services/system/security.service';
import { NetworkService } from '@core/services/system/network.service';
import { PosInfoService } from '@core/services/system/pos-info.service';
import { LocalStorage } from '@core/services/utils/localstorage.utils';
import { UserIdleService } from '@core/services/system/idle.service';
import { UPDATES_TYPES } from '@shared/constants/updates-types.const';
import { SERVER_CONFIG } from '@shared/constants/server.const';
import { AlertService } from '@core/services/system/alert.service';
import { LoadingService } from '@core/services/system/loading.service';
import { AppUpdateService } from '@core/services/system/app-update.service';
import { APP_INFO } from '@shared/constants/app-info.const';
import { AdminPageService } from '@core/services/system/admin-page.service';
import { ImageLoaderConfigService } from '@shared/components/ionic-image-loader-v5';
import { InvoicesService } from '@core/services/system/invoices.service';
import { CollectionViewService } from '@core/services/system/collection-view.service';
import { CHECKOUT_STEPS } from '@shared/constants/checkout-steps.const';
import { NullifyModalService } from '@core/services/system/nullify-modal.service';
import { TimApiService } from '@core/services/system/timApi.service';
import { LogService, CoreFields } from '@core/services/system/logger';
import { PosSettingsService } from '@core/services/system/pos-settings.service';
import { PAYMASH_PROFILE } from '@profile';
import { RouteNavigationService } from '@core/services/system/route-navigation/route-navigation.service';
import { ROUTE_URLS } from '@routing/route-urls.const';
import { PosSelfCheckService } from '@core/services/system/pos-self-check.service';
import { MyPosMiniFacade } from '@features/my-pos-mini/my-pos-mini.facade';
import { StorageKeys } from '@shared/constants/storage.const';
import { MyPosService } from '@core/services/system/devices/my-pos/my-pos.service';
import { SumUpService } from '@core/services/system/sumup/sumup.service';
import { KeyboardService } from '@core/services/system/keyboard/keyboard.service';
import { StatusBarService } from '@core/services/system/status-bar/status-bar.service';
import { CartService } from '@core/services/system/cart/cart.service';
import { NotifyPopupsService } from '@core/services/system/notify-popups.service';
import { CartInteractionCoordinator } from '@core/services/system/cart/cart-interaction.contracts';
import { DbDaoUtils, IntercomService } from '@core/services';
import { ActionSheetService } from '@core/services/system/action-sheet/action-sheet.service';
import { AuthApiService } from '@core/services/api/auth-api.service';
import { NgZoneService, TranslateSystemService } from '@core/services/system';
import { swiperRegister } from './swiper';
import { Company } from '@shared/classes/company.class';
import { PlatformService } from '@core/services/system/platform/platform.service';
import { ICustomerDisplayService } from '@core/services/system/customer-display';
import { EmployeesFacadeStore } from '@state/employees';
import { RootApplicationStateFacade } from '@state/root';
import { App, URLOpenListenerEvent } from '@core/capacitor-plugins/capacitor-plugins.class';
import { TargetDeviceEnum } from '@shared/constants';
import { merge, tap, map } from 'rxjs';
import { observeOn } from 'rxjs/operators';
import { Invoice } from '@shared/classes/invoice.class';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { Device as CapacitorDevice, DeviceInfo } from '@core/capacitor-plugins/capacitor-plugins.class';
import { NativeOnly } from '@shared/decorators/native-only.decor';

swiperRegister();

@Component({
  selector: 'app-paymash',
  encapsulation: ViewEncapsulation.None,
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppMainModule {
  private rootPage = ROUTE_URLS.login;
  private startSync = false;
  public appIsActive: boolean = true;
  public forceUserLogoutOnResume: boolean = false;
  public targetDevice: TargetDeviceEnum;
  private readonly logger = this.logService.createLogger('AppMainModule');

  constructor(
    public platform: Platform,
    private platformService: PlatformService,
    public dbDaoService: DbDaoService,
    private dbDaoUtils: DbDaoUtils,
    private updatesService: UpdatesService,
    public SyncService: SyncService,
    public SecurityService: SecurityService,
    public AdminPageService: AdminPageService,
    public NetworkService: NetworkService,
    public LocalStorage: LocalStorage,
    public TranslateService: TranslateService,
    private translateSystemService: TranslateSystemService,
    public UserIdleService: UserIdleService,
    public MenuController: MenuController,
    public ModalService: ModalService,
    public IntercomService: IntercomService,
    public PosInfoService: PosInfoService,
    public AlertService: AlertService,
    public LoadingService: LoadingService,
    private appUpdateService: AppUpdateService,
    public ImageLoaderConfig: ImageLoaderConfigService,
    public InvoicesService: InvoicesService,
    private NullifyModalService: NullifyModalService,
    public CollectionViewService: CollectionViewService,
    public MprimeService: TimApiService,
    public myPosMiniFacade: MyPosMiniFacade,
    private posSettingsService: PosSettingsService,
    private posSelfCheckService: PosSelfCheckService,
    private routeNavigationService: RouteNavigationService,
    private navController: NavController,
    private myPosService: MyPosService,
    private sumUpService: SumUpService,
    private keyboardService: KeyboardService,
    private statusBarService: StatusBarService,
    private cartService: CartService,
    private cartInteractionCoordinator: CartInteractionCoordinator,
    private notifyPopupsService: NotifyPopupsService,
    private actionSheetService: ActionSheetService,
    private authApiService: AuthApiService,
    private customerDisplayService: ICustomerDisplayService,
    private ngZoneService: NgZoneService,
    private logService: LogService,
    private cdr: ChangeDetectorRef,
    private employeeStateFacade: EmployeesFacadeStore,
    private rootStateFacade: RootApplicationStateFacade,
    private posInfoService: PosInfoService
  ) {
    this.checkLoggerEnable();
    this.ImageLoaderConfig.initConfiguration();
    this.routeNavigationService.loadRouting();
    this.initializeApp();
  }

  initializeApp() {
    // Secure Storage is initialized via APP_INITIALIZER before bootstrap, so it is
    // guaranteed ready here and in every downstream request/POS operation.
    const userIdle$ = this.UserIdleService.idleEvent.pipe(tap(() => this.checkOnUserIdleOccur()));

    const badRefreshToken$ = this.SecurityService.badRefreshToken.pipe(
      tap((data) => {
        this.logger.debug('badRefreshToken: data', { data });
        if (data.clearLocalDatabase) {
          this.resetCompany();
          return;
        }
        if (data.isExpiredVersion) {
          this.ngZoneService.run(() => this.appUpdateService.showRequiredUpdatePopup());
        }
        this.ngZoneService.run(() => this.logoutAndRedirectToLoginPage());
      })
    );

    const activeInvoicePaid$ = this.updatesService.activeInvoiceWasPaid.pipe(
      tap((invoice: Invoice) => {
        this.ngZoneService.run(() => {
          this.logger.info('invoice was paid on the other POS', { invoiceUuid: invoice?.uuid });
          this.setPageForPaidInvoiceOnOtherDevice();
        });
      })
    );

    const paymentMethodUpdate$ = this.updatesService.getUpdateEmmiterByType(UPDATES_TYPES.PaymentMethod.type).pipe(
      tap((paymentMethods) => {
        this.posSettingsService.handlePaymentMethodUpdate(paymentMethods);
      })
    );

    const productUpdate$ = this.updatesService.getUpdateEmmiterByType(UPDATES_TYPES.Product.type).pipe(
      tap((products) => {
        this.posSettingsService.handleTipProductUpdate(products);
      })
    );

    const pause$ = this.platform.pause.pipe(
      tap(() => {
        this.appIsActive = false;
        // Boundary: drop tracked adds when backgrounding so a stale entry can't gate a later flow.
        this.cartInteractionCoordinator.reset();
        // Persist any coalesced cart tap-adds before backgrounding (no-op if nothing pending).
        this.cartService.flushSave();
        // Stop the customer-display mDNS/Zeroconf resolver in the background: it is
        // useless when not foregrounded and keeps feeding the iOS Network.framework
        // resolver path (sa_dst_cache_create crash). Resumed below when needed.
        this.customerDisplayService.stopDeviceDiscovery();
      })
    );

    const resume$ = this.platform.resume.pipe(
      tap(() => {
        this.ngZoneService.run(() => {
          setTimeout(() => (this.appIsActive = true), 50);
          setTimeout(() => this.cdr.markForCheck(), 150);
        });

        // Restart discovery only when a display is still active/saved and needs reconnect.
        if (this.customerDisplayService.devices.some((d) => d.status !== 'disconnected')) {
          this.customerDisplayService.startDeviceDiscovery();
        }

        setTimeout(() => {
          if (this.forceUserLogoutOnResume) {
            this.logger.debug('initializeApp:platform:resume:checkOnUserIdleOccur');
            this.checkOnUserIdleOccur();
          }
        }, 500);
        this.restartSixTerminalApi();
      })
    );

    merge(userIdle$, badRefreshToken$, activeInvoicePaid$, paymentMethodUpdate$, productUpdate$, pause$, resume$)
      .pipe(observeOn(this.ngZoneService.leaveNgZoneRxjs()))
      .subscribe();

    this.platform
      .ready()
      .then(async () => {
        await this.detectedTargetDevice();
        const device: DeviceInfo & { version: string; uuid: string; serial: string } = this.posInfoService.getDeviceFullInfoSnapshot();
        const { model, version, manufacturer, serial, uuid } = device;

        const deviceInfo = `${model}:${version}:${serial ?? uuid}`;
        this.logger.info('manufacturer, deviceInfo', { manufacturer, deviceInfo });
        this.logService.setField(CoreFields.Device, deviceInfo);

        if (this.myPosService.hasModernWebView) {
          void this.checkWebViewVersion();
        }

        this.initHandleApplicationOpen();

        void this.runApp();
      })
      .catch((err) => this.logger.error(err, 'initializeApp:platform:ready'));
  }

  async runApp(): Promise<void> {
    try {
      this.fastBoot();
      this.deferredInit();
    } catch (err) {
      this.logger.error(err, 'RootRunApp:UnexpectedError');
    }
  }

  /* ----------------------------- FAST BOOT ----------------------------- */
  private fastBoot() {
    this.ngZoneService.runOutsideAngular(() => {
      this.checkPluginsState();
      this.setupAndroidBackButton();
      this.restartSumUp();
      this.setupStatusBar();
      this.setupKeyboardOutsideAngular();
      this.setupLogger();
      this.NetworkService.startNetworkMonitoring();
      void this.lockScreenOrientation();
    });
  }

  /* ------------------------ DEFERRED INIT ------------------------------ */
  private deferredInit() {
    setTimeout(async () => {
      await this.setDeviceFingerprintSafe();
      await this.initializeUserAndDatabase();
      this.measureDevicePerformance();
      this.initializeLocalization();
      this.checkMyPOSConnection();
      this.startAppUpdateScheduler();
      this.subscribeToSecurityEvents();
      void this.navigateToRootPage();
      this.routeNavigationService.setInitializationFinished(true);
    }, 50);
  }

  private checkPluginsState(): void {
    try {
      this.posSelfCheckService.checkPluginsState();
    } catch (e) {
      this.logger.error(e, 'runApp:posSelfCheckService:checkPluginsState');
    }
  }

  private async lockScreenOrientation(): Promise<void> {
    try {
      if (this.platform.is('android') && window.screen?.['orientation']) {
        const orientation = this.platform.width() < 768 ? 'portrait' : 'landscape';
        await ScreenOrientation.lock({ orientation });
      }
    } catch (e) {
      this.logger.warning('runApp:screen:orientation', { error: e });
    }
  }

  private async setDeviceFingerprintSafe(): Promise<void> {
    try {
      await this.SecurityService.setDeviceFingerprint();
    } catch (e) {
      this.logger.error(e, 'runApp:setFingerprint');
    }
  }

  private setupAndroidBackButton(): void {
    if (this.platform.is('android')) {
      this.platform.backButton.subscribeWithPriority(0, () => {});
    }
  }

  private setupKeyboardOutsideAngular(): void {
    this.keyboardService.setAccessoryBarVisible({ isVisible: true }).catch((error) => {
      this.logger.error(error, 'runApp:Keyboard:setAccessoryBarVisible');
    });
  }

  private setupLogger(): void {
    const deviceIdentifier = this.SecurityService.getDeviceIdentifier();
    const deviceFingerprint = this.SecurityService.getDeviceFingerprint();
    this.logService.setSentryTag(CoreFields.PosSystemUuid, deviceIdentifier);
    this.logService.setSentryTag(CoreFields.DeviceFingerprint, deviceFingerprint);
    this.logService.setField(CoreFields.PosSystemUuid, deviceIdentifier);
    this.logService.setField(CoreFields.AppVersion, `${APP_INFO.APP_VERSION}:${APP_INFO.APP_BUILD}`);
    this.logService.setField(CoreFields.System, `pm.pos.${PAYMASH_PROFILE.APP_ENV}`);
    this.logService.setField(CoreFields.AppEnv, PAYMASH_PROFILE.APP_ENV);
  }

  private async initializeUserAndDatabase(): Promise<void> {
    const userData = this.LocalStorage.get(StorageKeys.loggedUserData);
    if (!userData) {
      await this.handleLogout();
      return;
    }

    try {
      const parsedUserData = JSON.parse(userData);
      let accessToken = parsedUserData['accessToken'];
      let refreshToken = parsedUserData['refreshToken'];
      let company = parsedUserData['company'];
      let companyUuid = parsedUserData['company']['uuid'];
      if (!accessToken || !refreshToken || !companyUuid) throw new Error();

      this.SecurityService.setLoggedUserData(parsedUserData);
      this.PosInfoService.updatePosDataOnServer();

      await this.ensureDbVersion(company.uuid);
      await this.initDatabase(company.uuid);
      void this.InvoicesService.removeOldInvoicesFromLocalDb();
    } catch (err) {
      this.logger.error(err, 'runApp:initializeUserAndDatabase');
      await this.handleLogout();
    }
  }

  private async ensureDbVersion(companyUuid: string): Promise<void> {
    try {
      let dbVersion = await this.dbDaoUtils.getDbVersion(companyUuid);
      if (!dbVersion) {
        await this.dbDaoUtils.setDbVersion(APP_INFO.MIN_DB_VERSION, companyUuid);
      }
      if (parseInt(dbVersion) < parseInt(APP_INFO.MIN_DB_VERSION)) {
        const alert = await this.AlertService.create({
          header: this.TranslateService.instant('database_mismatch_title'),
          message: this.TranslateService.instant('database_mismatch_message'),
          buttons: [
            { text: this.TranslateService.instant('common_cancel'), role: 'cancel' },
            { text: this.TranslateService.instant('database_refresh'), handler: () => this.resetCompany() },
          ],
        });
        await alert.present().catch((err) => this.logger.error(err, 'runApp:DbDaoService.getDbVersion:alert:present'));
      }
    } catch (err) {
      this.logger.error(err, 'runApp:ensureDbVersion');
      this.dbDaoUtils
        .setDbVersion(APP_INFO.MIN_DB_VERSION, companyUuid)
        .catch((err) => this.logger.error(err, 'runApp:DbDaoService:getDbVersion:catch:DbDaoService:setDbVersion'));
    }
  }

  private async initDatabase(companyUuid: string): Promise<void> {
    this.LoadingService.showLoadingItem();
    try {
      const data = await this.dbDaoService.initDatabase(companyUuid);

      if (data) {
        this.startSync = true;
        this.rootPage = ROUTE_URLS.employee;
      }

      const companyData = await this.dbDaoService.getAllData(UPDATES_TYPES.Company.type);
      this.logger.debug('initDatabase:companyData:length', { length: companyData.data.length });
      const company = companyData.data[0];
      const hasLokiProblem = await this.hasLokiDbMigrationProblem(company, companyUuid);

      if (hasLokiProblem) {
        this.logger.info('hasLokiProblem should call resetCompany');
        this.updatesService.currentCompanyUuid = companyUuid;
        this.SecurityService.setLoggedCompanyData({ uuid: companyUuid });
        this.resetCompany(true);
        return;
      }

      this.SecurityService.setLoggedCompanyData(company);
      this.LoadingService.hideLoadingItem();
      this.restartSixTerminalApi();
      this.rootStateFacade.initLocalDBSuccess();
    } catch (error) {
      this.logger.error(error, 'runApp:initDatabase');
      this.rootPage = ROUTE_URLS.login;
      this.SecurityService.setLoggedUserData(null);
      void this.shutdownIntercom();
      this.LoadingService.hideLoadingItem();
    }
  }

  private measureDevicePerformance(): void {
    if (!this.PosInfoService.getDevicePerformance()) {
      const value = this.PosInfoService.performanceTest();
      this.PosInfoService.setDevicePerformance(value);
    }
  }

  private initializeLocalization(): void {
    this.translateSystemService.setDefaultLanguage();
  }

  private startAppUpdateScheduler(): void {
    this.appUpdateService.startUpdateScheduler(SERVER_CONFIG.APP_ENV);
  }

  @NativeOnly()
  private async shutdownIntercom(): Promise<void> {
    await this.IntercomService.shutdown().catch((err) => this.logger.error(err, 'runApp:IntercomService.shutdown'));
  }

  private subscribeToSecurityEvents(): void {
    this.ngZoneService.runOutsideAngular(() => {
      merge(
        this.SecurityService.logoutEvent.pipe(map((): string => 'logout')),
        this.SecurityService.resyncEvent.pipe(map((): string => 'resync'))
      ).subscribe((eventType: string): void => {
        this.ngZoneService.run((): void => {
          if (eventType === 'logout') {
            this.logoutAndRedirectToLoginPage();
            this.cdr.markForCheck();
          } else if (eventType === 'resync') {
            this.navController
              .navigateRoot(ROUTE_URLS.employee, { animated: true, animationDirection: 'back' })
              .catch((err) => this.logger.error(err, 'runApp:SecurityService:resyncEvent'));
            this.cdr.markForCheck();
          }
        });
      });
    });
  }

  private async navigateToRootPage(): Promise<void> {
    try {
      await this.navController.navigateRoot(this.rootPage, { state: { startSync: this.startSync } });
    } catch (err) {
      this.logger.error(err, 'runApp:navController:navigateRoot:rootPage');
    }
  }

  private async handleLogout(): Promise<void> {
    this.SecurityService.setLoggedUserData(null);
    void this.shutdownIntercom();
    this.rootPage = ROUTE_URLS.login;
  }

  // -----------------------------------------------------------------------------------------------

  private setPageForPaidInvoiceOnOtherDevice() {
    const pageInstance = this.routeNavigationService.current;
    const checkoutPageIsActive = pageInstance === ROUTE_URLS.checkout;

    if (checkoutPageIsActive) {
      this.CollectionViewService.setCurrentSidebarSlide(CHECKOUT_STEPS.SUCCESS);
      return;
    }

    const isOneClickPayment = this.posSettingsService.getOneClickPaymentStatus();
    const collectionPageIsActive = pageInstance === ROUTE_URLS.collection;
    if (isOneClickPayment && collectionPageIsActive) {
      this.cartService.setDataAfterSuccessPay();
      this.CollectionViewService.setCurrentSidebarSlide(CHECKOUT_STEPS.CHECKOUT);
      this.notifyPopupsService.launchNotification('invoice-paid');
      return;
    }

    const employeeSelectPageIsActive = pageInstance === ROUTE_URLS.employee;
    const isSuccessPage = this.CollectionViewService.getCurrentSidebarSlide() === CHECKOUT_STEPS.SUCCESS;
    if (!isSuccessPage && !checkoutPageIsActive && !employeeSelectPageIsActive) {
      this.navController
        .navigateForward(ROUTE_URLS.checkout, {
          animated: true,
          animation: mdTransitionAnimation,
          animationDirection: 'forward',
        })
        .then(() => {
          this.CollectionViewService.setCurrentSidebarSlide(CHECKOUT_STEPS.SUCCESS);
        })
        .catch((err) => this.logger.error(err, 'setPageForPaidInvoiceOnOtherDevice:navigateForward:checkout'));
    }
  }

  public checkMyPOSConnection() {
    if (this.myPosService.isMyPosTerminal) {
      const companyData = this.SecurityService.getLoggedCompanyData();
      const currency = companyData ? companyData.locale.currency : null;
      const language = this.TranslateService.currentLang;
      this.myPosMiniFacade.connectAndGetPrinter(currency, language).catch((err) => {
        this.logger.error(err, 'checkMyPOSConnection:connect');
        this.LocalStorage.remove(StorageKeys.activeTerminal);
      });
    }
  }

  public checkOnUserIdleOccur(): void {
    this.logger.debug('Start checkOnUserIdleOccur');
    this.MenuController.close().catch((err) => this.logger.error(err, 'logout:MenuController:close'));
    this.NullifyModalService.destroyModal();
    this.ModalService.dismissAll();
    this.AlertService.dismissAll();
    this.actionSheetService.dismiss();
    this.IntercomService.shutdown().catch((err) => this.logger.error(err, 'checkOnUserIdleOccur:IntercomService:shutdown'));
    this.InvoicesService.removeOldInvoicesFromLocalDb();
    if (this.employeeStateFacade.activeEmployeeSnapshot) {
      if (this.appIsActive) {
        this.logout();
      }
      this.forceUserLogoutOnResume = !this.appIsActive;
    }
  }

  private logout() {
    this.employeeStateFacade.clearActiveEmployee();
    this.ngZoneService.run(() =>
      this.navController
        .navigateForward(ROUTE_URLS.employee)
        .catch((err) => this.logger.error(err, 'logoutAndRedirectToLoginPage:navigateRoot:login'))
    );
  }

  public logoutAndRedirectToLoginPage(shouldReload: boolean = false) {
    this.authApiService.logout().catch((err) => this.logger.error(err, 'logoutAndRedirectToLoginPage:authApiService:logout'));
    this.SecurityService.setLoggedUserData(null);
    this.SecurityService.setActiveStore(null);
    this.employeeStateFacade.clearActiveEmployee();
    this.updatesService.stopUpdates();
    this.AdminPageService.logout();
    this.navController
      .navigateRoot(ROUTE_URLS.login, { animated: true, animationDirection: 'back' })
      .catch((err) => this.logger.error(err, 'logoutAndRedirectToLoginPage:navigateRoot:login'));
    if (shouldReload) {
      setTimeout(() => {
        window.location.reload();
      }, 400);
    }
  }

  public resetCompany(shouldReload: boolean = false) {
    this.SecurityService.resetCompany()
      .then(() => {
        const companyData = this.SecurityService.getLoggedCompanyData();
        if (companyData) {
          this.dbDaoUtils
            .setDbVersion(APP_INFO.MIN_DB_VERSION, companyData?.uuid)
            .catch((err) => this.logger.error(err, 'resetCompany:DbDaoService:setDbVersion'));
        }
        this.updatesService.removeLocalVersions();
        this.updatesService.removeLocalUpdatesLinks();
        this.posSettingsService.resetCompanyData();
        this.customerDisplayService.turnOff().catch();
        this.logoutAndRedirectToLoginPage(shouldReload);
        this.logger.info('Finish resetCompany');
      })
      .catch((err) => this.logger.error(err, 'resetCompany:resetCompany'));
  }

  private async checkWebViewVersion() {
    try {
      const info: DeviceInfo = await CapacitorDevice.getInfo();
      if (parseInt(info.webViewVersion.split('.')[0]) < APP_INFO.MIN_WEBVIEW_VERSION) {
        const alert = await this.AlertService.create({
          header: this.TranslateService.instant('webview_version_mismatch_title'),
          message: this.TranslateService.instant('webview_version_mismatch_message'),
          backdropDismiss: false,
        });
        alert.present().catch((err) => this.logger.error(err, 'runApp:checkWebViewVersion:alert:present'));
      }
    } catch (err) {
      this.logger.error(err, 'runApp:checkWebViewVersion:CapacitorDevice:getInfo');
    }
  }

  private setupStatusBar() {
    const style = this.statusBarService.getStatusBarStyle();
    this.statusBarService.setStyle({ style }).catch((error) => this.logger.error(error, 'setupStatusBar:setStyle'));

    this.statusBarService.setOverlaysWebView(false).catch((e) => this.logger.error(e, 'setupStatusBar:setOverlaysWebView'));

    this.statusBarService
      .setBackgroundColor({ color: '#16284D' })
      .catch((e) => this.logger.error(e, 'setupStatusBar:setBackgroundColor'));
  }

  private async hasLokiDbMigrationProblem(company: Company, companyUuid: string) {
    if (!!company || this.platformService.isWeb) {
      return false;
    }
    return this.dbDaoUtils.hasLokiDbFiles(companyUuid);
  }

  private initHandleApplicationOpen(): void {
    if (this.targetDevice !== TargetDeviceEnum.ADYEN && this.targetDevice !== TargetDeviceEnum.IOS) {
      // Cold-start links also arrive here (retained until the first listener), so App.getLaunchUrl() would duplicate them.
      App.addListener('appUrlOpen', (event: URLOpenListenerEvent): void => {
        this.handleApplicationUrl(event?.url);
      });
    }
  }

  private handleApplicationUrl(rawUrl: string): void {
    if (!rawUrl) {
      return;
    }

    try {
      this.rootStateFacade.appUrlOpen(new URL(rawUrl));
    } catch (error) {
      this.logger.error(error, 'initHandleApplicationOpen:parseUrl');
    }
  }

  @NativeOnly()
  private restartSixTerminalApi(): void {
    if (this.targetDevice !== TargetDeviceEnum.ADYEN) {
      this.MprimeService.reConnectToMprime();
    }
  }

  private restartSumUp(): void {
    if (this.targetDevice !== TargetDeviceEnum.ADYEN) {
      this.sumUpService.init().catch((): void => this.logger.debug('runApp:PaymentService:sumUpInit'));
    }
  }

  private async detectedTargetDevice(): Promise<void> {
    await this.posInfoService.detectedDeviceInfo();
    this.targetDevice = this.PosInfoService.detectedTargetDeviceModel();
    this.LocalStorage.set(StorageKeys.targetDevice, this.targetDevice);
  }

  private checkLoggerEnable(): void {
    const raw: string = this.LocalStorage.get(StorageKeys.enableLogs);

    if (raw === null || raw === undefined) {
      this.LocalStorage.setObject(StorageKeys.enableLogs, true);
      return;
    }

    const current: boolean = raw === 'true';
    this.LocalStorage.setObject(StorageKeys.enableLogs, current);
  }
}
